import React from 'react';
import Typography from '../Text/Typography';
import Banner from '../Banner/Banner';

/** Single routine-type explanation (AM or PM) from explanation API */
export interface RoutineTypeExplanation {
  source: string;
  explanation: {
    headline: string;
    bullets: Array<{ text: string; reasonCodesUsed?: string[] }>;
    perStepNotes?: Record<string, string>;
  };
  warnings: Array<{ code: string; routineType?: string; message?: string; category?: string }>;
}

export type ExplanationByRoutineType = { AM: RoutineTypeExplanation | null; PM: RoutineTypeExplanation | null };

export const ROUTINE_EXPLANATION_STORAGE_KEY = 'routineExplanationByRoutineType';

const hasScheduledTodayInBullets = (bullets: Array<{ reasonCodesUsed?: string[] }>) =>
  bullets.some((b) => Array.isArray(b.reasonCodesUsed) && b.reasonCodesUsed.includes('SCHEDULED_TODAY'));

export function RoutineExplanationBanner({
  routineType,
  data,
}: {
  routineType: 'AM' | 'PM';
  data: RoutineTypeExplanation;
}) {
  const { explanation, warnings } = data;
  const title = `Why this ${routineType === 'AM' ? 'AM' : 'PM'} routine?`;
  const summary = ''; // Headline (e.g. "A Friendly Saturday Morning...") removed from banner
  const bullets = (explanation.bullets || []).slice(0, 6);
  const showAcrossTheWeek = hasScheduledTodayInBullets(bullets);
  const content = (
    <>
      {bullets.length > 0 && (
        <section className="routine-explanation-section-block">
          <Typography variant="body-small-bold">Key reasons this week</Typography>
          <ul style={{ margin: '0.25rem 0 0 1.25rem', paddingLeft: 0 }}>
            {bullets.map((b, i) => (
              <li key={i} style={{ marginBottom: '0.25rem' }}>
                <Typography variant="body-small">{b.text}</Typography>
              </li>
            ))}
          </ul>
        </section>
      )}
      {warnings && warnings.length > 0 && (
        <section className="routine-explanation-section-block" style={{ marginTop: '0.75rem' }}>
          <Typography variant="body-small-bold">Things to review</Typography>
          <ul style={{ margin: '0.25rem 0 0 1.25rem', paddingLeft: 0 }}>
            {warnings.map((w, i) => (
              <li key={i}>
                <Typography variant="body-small">
                  {w.message || w.code}{w.category ? ` (${w.category})` : ''}
                </Typography>
              </li>
            ))}
          </ul>
        </section>
      )}
      {showAcrossTheWeek && (
        <div style={{ marginTop: '0.75rem' }}>
          <Typography variant="body-small">
            Some products appear only on certain days due to frequency scheduling.
          </Typography>
        </div>
      )}
    </>
  );
  return (
    <Banner
      variant="expandable-info"
      title={title}
      summary={summary}
      content={content}
      defaultExpanded={false}
      persistent={true}
    />
  );
}
